import { IInsightsPage, IInsightsPageTab } from '../../logic/api';
import { ISessionState } from '../../utility/useSession';
import { getFileUrl } from '../../utility/utils';


function isInsightsPageTab(obj: unknown): obj is IInsightsPageTab {
  if (!obj || typeof obj !== "object") {
    console.warn(`InsightsPageTab validation failed: ${obj} is not a valid object.`)
    return false
  }

  if ("id" in obj) {
    if (typeof (obj.id) !== "string") {
      console.warn(`InsightsPageTab validation failed: "id" is not a valid string, received: ${obj.id}.`)
      return false
    }
  }

  if ("tabTitle" in obj) {
    if (typeof (obj.tabTitle) !== "string") {
      console.warn(`InsightsPageTab validation failed: "tabTitle" is not a valid string, received: ${obj.tabTitle}.`)
      return false
    }
  }

  if ("value" in obj) {
    if (typeof (obj.value) !== "number") {
      console.warn(`InsightsPageTab validation failed: "value" is not a valid number, recevied ${obj.value}.`)
      return false
    }
  }

  if ("criteria" in obj) {
    if (!Array.isArray(obj.criteria)) {
      console.warn(`InsightsPageTab validation failed: "criteria" is not a valid array, received ${obj.criteria}.`)
      return false
    }

    if (!obj.criteria.every((criteria) => {
      if (!("type" in criteria)) {
        console.warn(`InsightsPageTab validation failed: "criteria" is missing a required "type" property.`)
        return false
      }
      if (typeof (criteria.type) !== "number") {
        console.warn(`InsightsPageTab validation failed: "criteria.type" is not a valid number, recevied ${criteria.type}.`)
        return false
      }
      if ("config" in criteria) {
        if (typeof (criteria.config) !== "object") {
          console.warn(`InsightsPageTab validation failed: "criteria.config" is not a valid object, received ${criteria.config}.`)
          return false
        }
      }

      return true
    })) {
      return false
    }
  }

  if ("charts" in obj) {
    if (!Array.isArray(obj.charts)) {
      console.warn(`InsightsPageTab validation failed: "charts" is not a valid array, recevied ${obj.charts}.`)
      return false
    }

    if (!obj.charts.every((chart) => {
      if (!("uuid" in chart)) {
        console.warn(`InsightsPageTab validation failed: "chart.uuid" is a missing a required property.`)
        return false
      }
      if (typeof (chart.uuid) !== "string") {
        console.warn(`InsightsPageTab validation failed: "chart.uuid" is not a valid string, received ${chart.uuid}.`)
        return false
      }
      if ("type" in chart) {
        if (typeof (chart.type) !== "number") {
          console.warn(`InsightsPageTab validation failed: "chart.type" is not a valid number, received ${chart.type}.`)
          return false
        }
      }
      if ("requiredConditions" in chart) {
        if (typeof chart.requiredConditions !== "object" || !chart.requiredConditions) {
          console.warn(`InsightsPageTab validation failed: "chart.requiredConditions" is not a valid object, received ${chart.requiredConditions}.`)
          return false
        }
        if ("preferences" in chart.requiredConditions) {
          if (typeof chart.requiredConditions.preferences !== "object" || !chart.requiredConditions.preferences) {
            console.warn(`InsightsPageTab validation failed: "chart.requiredConditions.preferences" is not a valid object, received ${chart.requiredConditions.preferences}.`)
            return false
          }
        }
      }

      return true
    })) {
      return false
    }
  }

  return true
}

function isInsightsPage(obj: unknown): obj is IInsightsPage {
  if (!obj || typeof obj !== "object") {
    console.warn(`InsightsPage validation failed: ${obj} is not a valid object.`)
    return false
  }

  if ("id" in obj) {
    if (typeof (obj.id) !== "string") {
      console.warn(`InsightsPage validation failed: "id" is not a valid string, received: ${obj.id}.`)
      return false
    }
  }

  if ("pageTitle" in obj) {
    if (typeof (obj.pageTitle) !== "string") {
      console.warn(`InsightsPage validation failed: "pageTitle" is not a valid string, received: ${obj.pageTitle}.`)
      return false
    }
  }

  if ("subTitle" in obj) {
    if (typeof (obj.subTitle) !== "string") {
      console.warn(`InsightsPage validation failed: "subTitle" is not a valid string, received: ${obj.subTitle}.`)
      return false
    }
  }

  if ("permission" in obj) {
    if (typeof (obj.permission) !== "string") {
      console.warn(`InsightsPage validation failed: "permission" is not a valid string, received: ${obj.permission}.`)
      return false
    }
  }

  if ("tabs" in obj) {
    if (!Array.isArray(obj.tabs)) {
      console.warn(`InsightsPage validation failed: "tabs" is not a valid array, received: ${obj.tabs}.`)
      return false
    }

    if (!obj.tabs.every(isInsightsPageTab)) {
      return false
    }
  }

  return true
}

// UploadedObjectService.getByBearerKey maps this exact token to root insights.json
// (PARK-13415). Update that mapping and its tests if this token or file changes.
const FILE_NAME = 'd52a3655ff31e9b3d86fe0cc37-b7a7355f33804a1724091c6a3672340c';

export async function getInsightsPages(session: ISessionState): Promise<IInsightsPage[]> {
  let data = undefined

  if (session.options.environment === "development") {
    data = (await import('./chart.json')).default;
  } else {
    const fileUrl = getFileUrl(session, FILE_NAME);
    const response = await fetch(fileUrl, {
      method: "GET",
      headers: session.current.context.headers,
    });
    data = await response.json()
  }

  if (!Array.isArray(data)) {
    console.warn("Could not load insights pages due to an invalid response.")
    return []
  }

  return data.filter(isInsightsPage)
}
